import { useFormik } from "formik";
import { find, get, isEmpty } from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  MdOutlineBadge,
  MdOutlineCake,
  MdOutlineDiversity1,
  MdOutlineEmail,
  MdOutlineFace,
  MdOutlinePhone,
} from "react-icons/md";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CONTACT_POINT_SYSTEM,
  CURRENCY_SYMBOL,
  DATE_FORMAT,
  PAYMENT_FREQUENCIES,
  RELATIONSHIP,
  TEMPLATE_TYPES,
  TEMPLATES,
} from "utils/constants";
import { costToFrench } from "utils/helpers/values";

import { Col, Row } from "antd";
import { BiIdCard } from "react-icons/bi";
import ActionButtons from "../Components/ActionButtons";
import DataProtection from "../Components/DataProtection";
import Header from "../Components/Header";
import Checkbox from "../Components/Input/Checkbox";
import Layout from "../Components/Layout";
import DocumentDownload from "./DocumentDownload";
import validation from "./validation";

function View({
  subscription,
  onSubmit,
  generalCost,
  updateProjectStatus,
  attachments,
  downloadFile,
  documentsLoading,
  onBack,
  translate,
}) {
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = t("DETAIL", { returnObjects: true });

  const getTelecom = (system) => {
    return find(get(subscription, "project.contact.telecoms", []), {
      system,
    });
  };

  const phoneTelecom = getTelecom(CONTACT_POINT_SYSTEM.PHONE);
  const emailTelecom = getTelecom(CONTACT_POINT_SYSTEM.EMAIL);

  // Get protected persons info
  const relatedPersons = get(
    subscription,
    "project.contact.relatedPersons",
    [],
  );
  const hasSpouse = relatedPersons.some(
    ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
  );
  const hasChildren = relatedPersons.some(
    ({ relationship }) => relationship === RELATIONSHIP.CHILD,
  );

  let protectedPersonsText = translate?.PROTECTED_PERSONS?.YOU || "Vous";
  if (hasSpouse && hasChildren) {
    protectedPersonsText =
      translate?.PROTECTED_PERSONS?.YOU_AND_FAMILY || "Vous et votre famille";
  } else if (hasSpouse) {
    protectedPersonsText =
      translate?.PROTECTED_PERSONS?.YOU_AND_SPOUSE || "Vous et votre conjoint";
  } else if (hasChildren) {
    protectedPersonsText =
      translate?.PROTECTED_PERSONS?.YOU_AND_CHILDREN ||
      "Vous et votre/vos enfant(s)";
  }

  const EDIT_BLOCK = [
    {
      icon: <MdOutlineFace size={24} />,
      label: translate?.EDIT_BLOCK?.YOU_ARE || "Vous êtes",
      value: `${get(subscription, "project.contact.user.firstname")} ${get(
        subscription,
        "project.contact.user.lastname",
      )}`,
      template:
        TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_MUTUAL_MOBILITY].ADHERENT_INFO,
      isBeforeLocked: true,
    },
    {
      icon: <MdOutlineCake size={24} />,
      label: translate?.EDIT_BLOCK?.BIRTH_DATE || "Date de naissance",
      value: `${moment(
        get(subscription, "project.contact.user.birthDate"),
      ).format(DATE_FORMAT)}`,
      template:
        TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_MUTUAL_MOBILITY]
          .ADHERENT_BIRTHDATE,
      isBeforeLocked: true,
    },
    {
      icon: <MdOutlinePhone size={24} />,
      label: translate?.EDIT_BLOCK?.PHONE || "N° de téléphone",
      value: get(phoneTelecom, "value", ""),
      template:
        TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_MUTUAL_MOBILITY]
          .ADHERENT_TELECOMS,
    },
    {
      icon: <MdOutlineEmail size={24} />,
      label: translate?.EDIT_BLOCK?.EMAIL || "Adresse email",
      value: get(emailTelecom, "value", ""),
      template:
        TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_MUTUAL_MOBILITY]
          .ADHERENT_TELECOMS,
    },
    {
      icon: <BiIdCard size={24} />,
      label: translate?.EDIT_BLOCK?.POSTAL_ADDRESS || "Adresse postale",
      value: (
        <>
          {get(
            find(get(subscription, "project.contact.telecoms"), {
              system: CONTACT_POINT_SYSTEM.ADDRESS,
            }),
            "value",
          )}
          <br />
          {`${get(
            find(get(subscription, "project.contact.telecoms"), {
              system: CONTACT_POINT_SYSTEM.CITY,
            }),
            "value",
          )} - ${get(
            find(get(subscription, "project.contact.telecoms"), {
              system: CONTACT_POINT_SYSTEM.ZIP_CODE,
            }),
            "value",
          )}`}
        </>
      ),
      template:
        TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_MUTUAL_MOBILITY]
          .ADHERENT_TELECOMS,
    },
    {
      icon: <MdOutlineBadge size={24} />,
      label:
        translate?.EDIT_BLOCK?.SOCIAL_SECURITY_NUMBER ||
        "N° de Sécurité sociale",
      value: get(subscription, "project.contact.socialSecurityNumber") || "",
      template:
        TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_MUTUAL_MOBILITY].ADHERENT_INFO,
    },
    {
      icon: <MdOutlineDiversity1 size={24} />,
      label:
        translate?.EDIT_BLOCK?.PROTECTED_PERSONS || "Personne(s) protégée(s)",
      value: protectedPersonsText,
      template:
        TEMPLATES.B2C.THEMES[TEMPLATE_TYPES.B2C_MUTUAL_MOBILITY]
          .RELATED_PERSONS,
    },
  ];

  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues: {
      additionalInfo: get(subscription, "additionalInfo"),
      project: {
        fields: {
          acceptProductStatuses: get(
            subscription,
            "project.fields.acceptProductStatuses",
            false,
          ),
          acceptAgreement: get(
            subscription,
            "project.fields.acceptAgreement",
            false,
          ),
          acceptFees: get(subscription, "project.fields.acceptFees", false),
        },
      },
    },
    onSubmit: onSubmit,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
    ),
    validateOnMount: true,
  });

  return (
    <Layout>
      <Header
        title={
          translate?.HEADER?.TITLE ||
          "Nous y sommes ! Votre contrat est prêt à être signé"
        }
        highlight={translate?.HEADER?.HIGHLIGHT || "Nous y sommes !"}
        subtitle={
          translate?.HEADER?.SUBTITLE || "Récapitulatif de vos informations"
        }
      />

      <div className="miltis-recap__wrapper">
        <Row justify={"center"} gutter={[24, 40]}>
          <Col xs={24} lg={10}>
            {/* Left Column: Offer Card */}
            <div className="miltis-recap__offer-card">
              <h3 className="miltis-recap__offer-title">
                {get(generalCost, "insurancePlan.name")}
              </h3>
              <div className="miltis-recap__offer-price">
                <div className="miltis-recap__offer-price--main">
                  <span className="miltis-recap__offer-price--amount">
                    {costToFrench(get(generalCost, "cost", 0))}{" "}
                    {CURRENCY_SYMBOL[get(generalCost, "currency")] || "€"}
                  </span>
                  <span className="miltis-recap__offer-price--period">
                    {translate?.OFFER?.PER_MONTH || "/mois"}
                  </span>
                </div>
                <div className="miltis-recap__offer-price--yearly">
                  {(
                    translate?.OFFER?.YEARLY ||
                    "soit une cotisation de {amount} {currency} / an"
                  )
                    .replace(
                      "{amount}",
                      costToFrench(get(generalCost, "cost", 0) * 12),
                    )
                    .replace(
                      "{currency}",
                      CURRENCY_SYMBOL[get(generalCost, "currency")] || "€",
                    )}
                </div>
              </div>
              {get(
                subscription,
                `project.fields.upsells.${get(
                  subscription,
                  "insurancePlan.id",
                )}`,
                [],
              ).length > 0 && (
                <>
                  <hr className="miltis-recap__divider" />

                  <div className="miltis-recap__includes">
                    <p className="miltis-recap__includes--title">
                      {translate?.OFFER?.INCLUDES_TITLE ||
                        "Inclut dans le prix"}
                    </p>
                    <div className="miltis-recap__includes--list">
                      {get(
                        subscription,
                        `project.fields.upsells.${get(
                          subscription,
                          "insurancePlan.id",
                        )}`,
                        [],
                      ).map(({ insurancePlan: { name }, cost, currency }) => (
                        <div
                          key={name}
                          className="miltis-recap__includes--item"
                        >
                          <span className="miltis-recap__includes--name">
                            {name}
                          </span>
                          <span className="miltis-recap__includes--price">
                            {cost} {CURRENCY_SYMBOL[currency] || "€"} / mois
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Documents Download Section */}
            <div className="miltis-recap__documents">
              <p className="miltis-recap__documents--title">
                Vous pouvez télécharger et consulter les documents relatifs à
                votre adhésion
              </p>
              <DocumentDownload
                items={attachments}
                download={(id) => downloadFile(id)}
              />
            </div>
          </Col>

          <Col xs={24} lg={1}>
            <div className="miltis-recap__vertical-divider" />
          </Col>

          <Col xs={24} lg={10}>
            {/* Right Column: Adherent Information */}
            <div className="miltis-recap__info-section">
              {/* Date d'effet */}
              <div className="miltis-recap__info-block">
                <p className="miltis-recap__info-title">
                  Date d'effet souhaitée
                </p>
                <div className="miltis-recap__info-item">
                  <div className="miltis-recap__info-content">
                    <span className="miltis-recap__info-label">
                      Date d'effet
                    </span>
                    <span className="miltis-recap__info-value">
                      {moment(
                        get(subscription, "project.contract.issuanceDate"),
                      ).format(DATE_FORMAT)}
                    </span>
                  </div>
                  <button
                    className="miltis-recap__info-edit"
                    onClick={() =>
                      updateProjectStatus({
                        template:
                          TEMPLATES.B2C.THEMES[
                            TEMPLATE_TYPES.B2C_MUTUAL_MOBILITY
                          ].EFFECTIVE_DATE,
                      })
                    }
                  >
                    Modifier
                  </button>
                </div>
              </div>

              {/* Informations de l'adhérent */}
              <div className="miltis-recap__info-block">
                <p className="miltis-recap__info-title">
                  Informations de l'adhérent
                </p>
                <div className="miltis-recap__info-list">
                  {EDIT_BLOCK.map(
                    ({
                      icon,
                      label,
                      value,
                      template,
                      isBeforeLocked = false,
                    }) => (
                      <div className="miltis-recap__info-item" key={label}>
                        <div className="miltis-recap__info-content">
                          <span className="miltis-recap__info-label">
                            {label}
                          </span>
                          {typeof value === "string" ? (
                            <span className="miltis-recap__info-value">
                              {value}
                            </span>
                          ) : (
                            <div className="miltis-recap__info-value">
                              {value}
                            </div>
                          )}
                        </div>
                        <button
                          className="miltis-recap__info-edit"
                          onClick={() =>
                            updateProjectStatus({
                              template,
                              isBeforeLocked,
                            })
                          }
                        >
                          Modifier
                        </button>
                      </div>
                    ),
                  )}

                  {/* Informations bancaires */}
                  <div className="miltis-recap__info-item">
                    <div className="miltis-recap__info-content">
                      <span className="miltis-recap__info-label">
                        Informations bancaires
                      </span>
                      <div className="miltis-recap__info-value">
                        <div>
                          <strong>Titulaire du compte :</strong>{" "}
                          {`${get(
                            subscription,
                            "project.contact.user.firstname",
                          )} ${get(
                            subscription,
                            "project.contact.user.lastname",
                          )}`}
                        </div>
                        <div>
                          <strong>Périodicité du prélèvement :</strong>{" "}
                          {TRANSLATION.PAYMENT_FREQUENCY.OPTIONS[
                            get(
                              subscription,
                              "payment.frequency",
                              PAYMENT_FREQUENCIES.MONTHLY,
                            )
                          ] ||
                            get(
                              subscription,
                              "payment.frequency",
                              PAYMENT_FREQUENCIES.MONTHLY,
                            )}
                        </div>
                        <div>
                          <strong>IBAN :</strong>{" "}
                          {get(subscription, "payment.payer.IBAN", "")}
                        </div>
                        <div>
                          <strong>BIC :</strong>{" "}
                          {get(subscription, "payment.payer.BIC", "")}
                        </div>
                      </div>
                    </div>
                    <button className="miltis-recap__info-edit">
                      Modifier
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {/* Consent Checkboxes */}
        <div className="miltis-recap__consent">
          <p className="miltis-recap__consent--intro">
            Nous vous remercions de bien vouloir prendre connaissance de la
            déclaration d'adhésion à la mutuelle et de cocher les cases
            correspondantes ci-dessous :
          </p>
          <div className="miltis-recap__consent--checkboxes">
            <Checkbox
              variant="consent"
              checked={
                get(values, "project.fields.acceptProductStatuses") || false
              }
              onChange={(checked) =>
                setFieldValue("project.fields.acceptProductStatuses", checked)
              }
            >
              Je déclare avoir reçu et pris connaissance, préalablement à
              l'adhésion au contrat, du document d'information sur le produit
              d'assurance complémentaire santé, des statuts, du règlement
              mutualiste, des dispositions particulières, de la notice
              d'information contractuelle Prévention et conseil Médical en ligne
              à la garantie d'assistance et en accepter tous les termes.
            </Checkbox>
            <Checkbox
              variant="consent"
              checked={get(values, "project.fields.acceptAgreement") || false}
              onChange={(checked) =>
                setFieldValue("project.fields.acceptAgreement", checked)
              }
            >
              J'atteste sur l'honneur l'exactitude des renseignements complétés.
            </Checkbox>
            <Checkbox
              variant="consent"
              checked={get(values, "project.fields.acceptFees") || false}
              onChange={(checked) =>
                setFieldValue("project.fields.acceptFees", checked)
              }
            >
              Je suis informée que la communication des documents contractuels
              ainsi que les informations relatives à l'exécution de mon contrat
              d'assurance (communication, avis d'échéance etc.) est réalisée
              exclusivement de manière électronique. J'atteste que l'adresse
              e-mail transmise est valide et me permet de recevoir ces
              informations.
            </Checkbox>
          </div>
        </div>

        {/* Action Buttons */}
        <ActionButtons
          onBack={onBack}
          onNext={handleSubmit}
          nextText="Je signe mon contrat"
          nextProps={{
            disabled: !isEmpty(formProps.errors) || documentsLoading,
            loading: documentsLoading,
          }}
          infoText={
            "Vous bénéficiez de la possibilité de <strong>renoncer à votre adhésion dans un délai de 14 jours</strong> calendaires à compter de la signature de votre bulletin d'adhésion"
          }
        />

        {/* Data Protection */}
        <DataProtection />
      </div>
    </Layout>
  );
}

export default View;
